import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { formatNumber, formatRelative } from '../format';
import type { AiProviderId, AiProviderStatus, AiSettingsResponse, CallQaStatus, LocationSummary, RatingStatus, TimeZoneSettings } from '../types';
import { GhlAppCard } from '../components/GhlAppCard';
import { UsersCard } from '../components/UsersCard';
import { TimeZoneCard } from '../components/TimeZoneCard';

function message(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : String(cause);
}

/** One provider: its key, its model, and proof that both work. */
function ProviderPanel({
  provider,
  active,
  onChanged,
}: {
  provider: AiProviderStatus;
  active: boolean;
  onChanged: (next: AiSettingsResponse) => void;
}) {
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(provider.model ?? provider.defaultModel);
  const [models, setModels] = useState<string[]>([]);
  const [busy, setBusy] = useState<'save' | 'test' | 'models' | 'remove' | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => setModel(provider.model ?? provider.defaultModel), [provider.model, provider.defaultModel]);

  const loadModels = useCallback(async () => {
    setBusy('models');
    try {
      setModels((await api.aiModels(provider.id)).models);
    } catch (cause) {
      setResult({ ok: false, text: message(cause) });
    } finally {
      setBusy(null);
    }
  }, [provider.id]);

  // A configured provider lists its real models straight away.
  useEffect(() => {
    if (provider.configured) void loadModels();
  }, [provider.configured, loadModels]);

  async function save() {
    setBusy('save');
    setResult(null);
    try {
      const next = await api.saveAiProvider(provider.id, {
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        model: model.trim() || provider.defaultModel,
      });
      if (next.models?.length) setModels(next.models);
      setApiKey('');
      setResult({ ok: true, text: apiKey.trim() ? 'Key checked and saved.' : 'Model saved.' });
      onChanged(next);
    } catch (cause) {
      setResult({ ok: false, text: message(cause) });
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setBusy('test');
    setResult(null);
    try {
      const outcome = await api.testAiProvider(provider.id);
      setResult(
        outcome.ok
          ? { ok: true, text: `Working — ${outcome.model} answered in ${((outcome.durationMs ?? 0) / 1000).toFixed(1)}s.` }
          : { ok: false, text: outcome.error ?? 'The test failed.' },
      );
      onChanged(await api.aiSettings());
    } catch (cause) {
      setResult({ ok: false, text: message(cause) });
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove the saved ${provider.label} key?`)) return;
    setBusy('remove');
    try {
      onChanged(await api.deleteAiProvider(provider.id));
      setModels([]);
      setResult(null);
    } catch (cause) {
      setResult({ ok: false, text: message(cause) });
    } finally {
      setBusy(null);
    }
  }

  const options = [...new Set([...(models.length ? models : provider.suggestedModels), model].filter(Boolean))];
  const modelChanged = provider.configured && model.trim() !== (provider.model ?? provider.defaultModel);

  return (
    <div className="provider-panel">
      <div className="dialog-grid">
        <div className="field full">
          <label htmlFor={`key-${provider.id}`}>
            API key {provider.configured ? `(saved, ends ${provider.keyHint})` : ''}
          </label>
          <input
            id={`key-${provider.id}`}
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder={provider.configured ? 'Paste a new key to replace the saved one' : provider.keyPlaceholder}
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
          <span className="small muted">
            Get one at{' '}
            <a href={provider.keyUrl} target="_blank" rel="noreferrer">
              {new URL(provider.keyUrl).hostname}
            </a>
            . It is checked with {provider.label.split(' ')[0]} before it is saved, stored encrypted, and never shown again.
          </span>
        </div>
        <div className="field full">
          <label htmlFor={`model-${provider.id}`}>Model</label>
          <div className="row nowrap-row">
            <input
              id={`model-${provider.id}`}
              type="text"
              list={`models-${provider.id}`}
              value={model}
              onChange={(event) => setModel(event.target.value)}
              style={{ flex: '1 1 260px' }}
            />
            <datalist id={`models-${provider.id}`}>
              {options.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            {provider.configured ? (
              <button type="button" className="btn btn-sm" disabled={busy !== null} onClick={loadModels}>
                {busy === 'models' ? 'Loading…' : 'Refresh list'}
              </button>
            ) : null}
          </div>
          <span className="small muted">
            {models.length
              ? `${models.length} models available to this key — type to search.`
              : `Suggested: ${provider.suggestedModels.join(', ')}. The full list loads once a key is saved.`}
          </span>
        </div>
      </div>

      {result ? <div className={`inline-result ${result.ok ? 'ok' : 'bad'}`}>{result.text}</div> : null}
      {!result && provider.lastError ? <div className="inline-result bad">Last error: {provider.lastError}</div> : null}

      <div className="row" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy !== null || (!apiKey.trim() && !modelChanged)}
          onClick={save}
        >
          {busy === 'save' ? 'Checking…' : provider.configured ? 'Save' : 'Save key'}
        </button>
        {provider.configured ? (
          <>
            <button type="button" className="btn" disabled={busy !== null} onClick={test}>
              {busy === 'test' ? 'Testing…' : 'Test connection'}
            </button>
            <button type="button" className="btn btn-danger" disabled={busy !== null} onClick={remove}>
              Remove key
            </button>
          </>
        ) : null}
        {provider.lastVerifiedAt ? (
          <span className="small muted">Last worked {formatRelative(provider.lastVerifiedAt)}</span>
        ) : null}
        {active ? <span className="tag">● In use for summaries</span> : null}
      </div>
    </div>
  );
}

/** Settings → AI: the provider and key summaries use, and what they may read. */
type Section = 'general' | 'ai' | 'app' | 'users';

/** Settings: AI, the HighLevel app, and (for owners) the people who can sign in. */
export function Settings({
  canManageUsers,
  locations,
  timeZones,
  onTimeZonesSaved,
}: {
  canManageUsers: boolean;
  locations: LocationSummary[];
  timeZones: TimeZoneSettings;
  onTimeZonesSaved: (next: TimeZoneSettings) => void;
}) {
  const [section, setSection] = useState<Section>('general');
  const sections: [Section, string][] = [
    ['general', 'General'],
    ['ai', 'AI'],
    ['app', 'HighLevel app'],
    ...(canManageUsers ? ([['users', 'Users & permissions']] as [Section, string][]) : []),
  ];
  return (
    <div className="stack">
      <div className="segmented" role="tablist" aria-label="Settings sections" style={{ alignSelf: 'flex-start' }}>
        {sections.map(([id, label]) => (
          <button type="button" role="tab" key={id} aria-pressed={section === id} onClick={() => setSection(id)}>
            {label}
          </button>
        ))}
      </div>
      {section === 'general' ? <TimeZoneCard settings={timeZones} locations={locations} onSaved={onTimeZonesSaved} /> : null}
      {section === 'ai' ? <AiSettings /> : null}
      {section === 'app' ? <GhlAppCard /> : null}
      {section === 'users' && canManageUsers ? <UsersCard locations={locations} /> : null}
    </div>
  );
}

function AiSettings() {
  const [settings, setSettings] = useState<AiSettingsResponse | null>(null);
  const [selected, setSelected] = useState<AiProviderId>('anthropic');
  const [context, setContext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  useEffect(() => {
    api
      .aiSettings()
      .then((response) => {
        setSettings(response);
        setContext(response.businessContext);
        setSelected(response.activeProvider ?? response.providers.find((item) => item.configured)?.id ?? 'anthropic');
      })
      .catch((cause) => setError(message(cause)));
  }, []);

  async function update(patch: Parameters<typeof api.saveAiSettings>[0], note: string) {
    setError(null);
    try {
      const next = await api.saveAiSettings(patch);
      setSettings((current) => ({ ...next, usage30d: current?.usage30d }));
      setSavedNote(note);
      setTimeout(() => setSavedNote(null), 2500);
    } catch (cause) {
      setError(message(cause));
    }
  }

  if (!settings) return error ? <div className="banner error">{error}</div> : <p className="empty">Loading settings…</p>;

  const provider = settings.providers.find((item) => item.id === selected)!;
  const usage = settings.usage30d ?? [];

  return (
    <div className="stack">
      {error ? (
        <div className="banner error">
          <span>{error}</span>
          <button type="button" className="btn btn-sm" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {savedNote ? <div className="banner">{savedNote}</div> : null}

      <div className="card">
        <div className="card-head">
          <h2>AI provider</h2>
          <span className={`tag ${settings.ready ? '' : 'muted'}`}>
            <span className="dot" style={{ background: settings.ready ? 'var(--outcome-answered)' : 'var(--ink-muted)' }} />
            {settings.ready
              ? `Summaries use ${settings.providers.find((item) => item.id === settings.activeProvider)?.label}`
              : 'Not set up'}
          </span>
        </div>
        <p className="card-sub">
          AI writes the sub-account summaries (Sub-accounts tab, or Overview with one sub-account selected) and the
          lead summaries (open any contact). Add a key for one provider; summaries are only generated when you click,
          and each is saved so viewing it again costs nothing.
        </p>

        <div className="provider-grid" role="radiogroup" aria-label="AI provider">
          {settings.providers.map((item) => (
            <button
              type="button"
              key={item.id}
              role="radio"
              aria-checked={selected === item.id}
              className={`provider-card${selected === item.id ? ' selected' : ''}`}
              onClick={() => setSelected(item.id)}
            >
              <span className="provider-name">{item.label}</span>
              <span className="provider-status">
                {item.configured ? (
                  <>
                    <span className="dot" style={{ background: item.lastError ? 'var(--danger-text)' : 'var(--outcome-answered)' }} />
                    Key ••••{item.keyHint?.slice(-4)} · {item.model ?? item.defaultModel}
                  </>
                ) : (
                  'No key'
                )}
              </span>
              {settings.activeProvider === item.id ? <span className="provider-badge">In use</span> : null}
            </button>
          ))}
        </div>

        <ProviderPanel
          key={provider.id}
          provider={provider}
          active={settings.activeProvider === provider.id}
          onChanged={(next) => setSettings((current) => ({ ...next, usage30d: current?.usage30d }))}
        />

        {provider.configured && settings.activeProvider !== provider.id ? (
          <div className="row" style={{ marginTop: 10 }}>
            <button
              type="button"
              className="btn"
              onClick={() => update({ activeProvider: provider.id }, `Summaries now use ${provider.label}.`)}
            >
              Use {provider.label} for summaries
            </button>
          </div>
        ) : null}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>What lead summaries may read</h2>
        </div>
        <p className="card-sub">
          A lead summary sends the contact’s history to {settings.ready ? 'the provider above' : 'your AI provider'}: their
          form answers, pipeline, appointments and call log always, and the sources below if they are on. Phone numbers and
          email addresses are never sent. Check the provider’s data terms before sending client conversations.
        </p>
        <div className="stack" style={{ gap: 6 }}>
          {(
            [
              ['messages', 'Messages and emails', 'SMS, chat and email threads with the contact'],
              ['notes', 'Notes', 'Notes your team wrote on the contact in HighLevel'],
              ['transcripts', 'Call transcripts', 'Transcripts of up to three recent answered calls, where HighLevel has them'],
            ] as const
          ).map(([key, label, hint]) => (
            <label className="switch" key={key}>
              <input
                type="checkbox"
                role="switch"
                checked={settings.include[key]}
                onChange={(event) =>
                  update({ include: { ...settings.include, [key]: event.target.checked } }, `${label} ${event.target.checked ? 'included' : 'excluded'}.`)
                }
              />
              <span>
                <strong>{label}</strong> <span className="muted">— {hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>About your agency</h2>
        </div>
        <p className="card-sub">
          Added to every AI request — summaries, ratings and the Assistant — for all sub-accounts: what the agency does,
          the terms your team uses, what to flag. Each client’s own business is described per sub-account under{' '}
          <strong>Sub-accounts → AI setup</strong>, and is added alongside this.
        </p>
        <textarea
          aria-label="Business context"
          value={context}
          maxLength={4000}
          placeholder="e.g. We run Facebook lead funnels for DSCR and fix-and-flip lenders. A good lead owns rental property, has 680+ credit and needs $150k+. Flag anyone who hasn't been called within an hour."
          onChange={(event) => setContext(event.target.value)}
        />
        <div className="row" style={{ marginTop: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={context === settings.businessContext}
            onClick={() => update({ businessContext: context }, 'Business context saved.')}
          >
            Save
          </button>
          <span className="small muted">{context.length} / 4000</span>
        </div>
      </div>

      <RatingCard settings={settings} onSaved={(next, note) => {
        setSettings((current) => ({ ...next, usage30d: current?.usage30d }));
        setSavedNote(note);
        setTimeout(() => setSavedNote(null), 2500);
      }} />

      <CallQaCard settings={settings} onSaved={(next, note) => {
        setSettings((current) => ({ ...next, usage30d: current?.usage30d }));
        setSavedNote(note);
        setTimeout(() => setSavedNote(null), 2500);
      }} />

      <div className="card">
        <div className="card-head">
          <h2>Usage · last 30 days</h2>
        </div>
        {usage.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Used for</th>
                  <th>Provider</th>
                  <th>Model</th>
                  <th className="num">Requests</th>
                  <th className="num">Summaries / leads</th>
                  <th className="num">Input tokens</th>
                  <th className="num">Output tokens</th>
                </tr>
              </thead>
              <tbody>
                {usage.map((row) => (
                  <tr key={`${row.kind}-${row.provider}-${row.model}`}>
                    <td>{row.kind === 'rating' ? 'Lead rating' : row.kind === 'call_qa' ? 'Call QA' : row.kind === 'assistant' ? 'Assistant' : 'Summaries'}</td>
                    <td>{settings.providers.find((item) => item.id === row.provider)?.label ?? row.provider}</td>
                    <td className="mono small">{row.model}</td>
                    <td className="num">{formatNumber(row.requests)}</td>
                    <td className="num">{formatNumber(row.items)}</td>
                    <td className="num">{formatNumber(row.inputTokens)}</td>
                    <td className="num">{formatNumber(row.outputTokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty">No AI requests yet.</p>
        )}
      </div>
    </div>
  );
}

/** Settings for the background AI lead rating, and where it has got to. */
function RatingCard({
  settings,
  onSaved,
}: {
  settings: AiSettingsResponse;
  onSaved: (next: AiSettingsResponse, note: string) => void;
}) {
  const [status, setStatus] = useState<RatingStatus | null>(null);
  const [limit, setLimit] = useState(String(settings.rating.dailyLimit));
  const [model, setModel] = useState(settings.rating.model);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .ratingStatus()
      .then((next) => {
        setStatus(next);
        setPrompt((current) => current ?? (next.prompt.trim() || next.defaultPrompt));
      })
      .catch(() => undefined);
  }, []);
  useEffect(refresh, [refresh]);
  useEffect(() => {
    if (!status?.running) return;
    const timer = setInterval(refresh, 3000);
    return () => clearInterval(timer);
  }, [status?.running, refresh]);

  async function save(patch: Partial<AiSettingsResponse['rating']>, message: string) {
    try {
      onSaved(await api.saveAiSettings({ rating: patch }), message);
      refresh();
    } catch (cause) {
      setNote(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  const savedPrompt = status ? status.prompt.trim() || status.defaultPrompt : '';
  const promptChanged = prompt !== null && prompt.trim() !== savedPrompt.trim();

  async function savePrompt(value: string) {
    if (!status) return;
    // Saving the default text stores nothing, so later improvements to the default still apply.
    const stored = value.trim() === status.defaultPrompt.trim() ? '' : value.trim();
    await save({ prompt: stored }, 'Rating prompt saved.');
    setPrompt(value.trim() || status.defaultPrompt);
    if (
      status.rated > 0 &&
      window.confirm(
        'Re-rate the leads of every sub-account that uses the default prompt? Leads are re-rated in the background, within the daily limit.',
      )
    ) {
      await rerateAll('default');
    }
  }

  async function rerateAll(scope?: 'default') {
    setNote(null);
    try {
      const { marked, status: next } = await api.resetRatings(scope);
      setStatus(next);
      setNote(`${formatNumber(marked)} leads will be re-rated with the current prompt, within the daily limit.`);
    } catch (cause) {
      setNote(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  async function runNow() {
    setBusy(true);
    setNote(null);
    try {
      const { result, status: next } = await api.runRatings();
      setStatus(next);
      setNote(
        result.skipped
          ? `Not run: ${result.skipped}.`
          : result.error
            ? result.error
            : `Rated ${formatNumber(result.rated ?? 0)} leads${result.failed ? `, ${result.failed} could not be rated` : ''}.`,
      );
    } catch (cause) {
      setNote(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const active = settings.providers.find((item) => item.id === settings.activeProvider);
  const modelOptions = active ? [...new Set([...active.suggestedModels, active.model ?? active.defaultModel])] : [];

  return (
    <div className="card">
      <div className="card-head">
        <h2>AI lead rating</h2>
        <label className="switch">
          <input
            type="checkbox"
            role="switch"
            checked={settings.rating.enabled}
            onChange={(event) =>
              save({ enabled: event.target.checked }, `AI lead rating ${event.target.checked ? 'on' : 'off'}.`)
            }
          />
          {settings.rating.enabled ? 'On' : 'Off'}
        </label>
      </div>
      <p className="card-sub">
        Rates every lead 0–10 for how likely they are to close, following the rating prompt below, from their form
        answers, tags, pipeline stage, calls, appointments and recent messages. It is the only lead rating the dashboard
        shows. It runs in the background every 10 minutes, 15 leads per AI request, newest leads first, and re-rates a
        lead when something new happens. Generating a lead summary also updates the rating.
      </p>

      {status ? (
        <div className="grid grid-kpi" style={{ marginBottom: 14 }}>
          <div className="stat">
            <span className="stat-label">Leads rated</span>
            <span className="stat-value">{formatNumber(status.rated)}</span>
            <span className="stat-foot">
              {status.averageScore !== null ? `average ${status.averageScore}/10 · ` : ''}
              {formatNumber(status.ratedSevenPlus)} rated 7+
            </span>
          </div>
          <div className="stat">
            <span className="stat-label">Waiting</span>
            <span className="stat-value">{formatNumber(status.waiting)}</span>
            <span className="stat-foot">new, or changed since rated</span>
          </div>
          <div className="stat">
            <span className="stat-label">Rated in last 24h</span>
            <span className="stat-value">{formatNumber(status.ratedLast24h)}</span>
            <span className="stat-foot">limit {formatNumber(status.dailyLimit)} a day</span>
          </div>
          <div className="stat">
            <span className="stat-label">Last run</span>
            <span className="stat-value" style={{ fontSize: 18 }}>
              {status.running ? 'Running…' : status.lastRun ? formatRelative(status.lastRun.at) : 'Not yet'}
            </span>
            <span className="stat-foot">
              {status.lastRun
                ? status.lastRun.error
                  ? `Stopped: ${status.lastRun.error}`
                  : `${formatNumber(status.lastRun.rated)} rated (${status.lastRun.trigger})`
                : settings.ready
                  ? 'First run within 10 minutes of start'
                  : 'Needs an AI key'}
            </span>
          </div>
        </div>
      ) : null}

      <div className="filters-body" style={{ padding: 0 }}>
        <div className="field">
          <label htmlFor="rating-window">Rate leads active in the last</label>
          <select
            id="rating-window"
            value={settings.rating.windowDays}
            onChange={(event) => save({ windowDays: Number(event.target.value) }, 'Rating window saved.')}
          >
            {[30, 60, 90, 180, 365].map((days) => (
              <option key={days} value={days}>
                {days} days
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="rating-limit">Most leads a day</label>
          <div className="row nowrap-row">
            <input
              id="rating-limit"
              type="text"
              inputMode="numeric"
              style={{ width: 110 }}
              value={limit}
              onChange={(event) => setLimit(event.target.value.replace(/\D/g, ''))}
            />
            {Number(limit) !== settings.rating.dailyLimit && limit !== '' ? (
              <button type="button" className="btn btn-sm" onClick={() => save({ dailyLimit: Number(limit) }, 'Daily limit saved.')}>
                Save
              </button>
            ) : null}
          </div>
        </div>
        <div className="field field-grow">
          <label htmlFor="rating-model">Rating model (optional)</label>
          <div className="row nowrap-row">
            <input
              id="rating-model"
              type="text"
              list="rating-models"
              placeholder={active ? `Same as summaries: ${active.model ?? active.defaultModel}` : 'Set up a provider first'}
              value={model}
              onChange={(event) => setModel(event.target.value)}
              style={{ flex: '1 1 220px' }}
            />
            <datalist id="rating-models">
              {modelOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            {model.trim() !== settings.rating.model ? (
              <button type="button" className="btn btn-sm" onClick={() => save({ model: model.trim() }, 'Rating model saved.')}>
                Save
              </button>
            ) : null}
          </div>
          <span className="small muted">A smaller, cheaper model is usually plenty for rating.</span>
        </div>
      </div>

      {status && prompt !== null ? (
        <div className="field" style={{ marginTop: 14 }}>
          <label htmlFor="rating-prompt">Default rating prompt</label>
          <textarea
            id="rating-prompt"
            rows={14}
            maxLength={6000}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            style={{ fontFamily: 'inherit' }}
          />
          <span className="small muted">
            Tells the AI how to score a lead — what matters most (e.g. credit score, funding required, type of property)
            and what each score means. The lead’s data and the 0–10 answer format are added automatically. Sub-accounts
            use this unless they have their own prompt (Sub-accounts → AI setup).
            {status.prompt.trim() ? '' : ' This is the built-in prompt; edit it to make it yours.'}
          </span>
          <div className="row">
            <button type="button" className="btn btn-sm btn-primary" disabled={!promptChanged || !prompt.trim()} onClick={() => savePrompt(prompt)}>
              Save prompt
            </button>
            {promptChanged ? (
              <button type="button" className="btn btn-sm" onClick={() => setPrompt(savedPrompt)}>
                Discard changes
              </button>
            ) : null}
            {status.prompt.trim() ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  if (window.confirm('Go back to the built-in rating prompt?')) void savePrompt(status.defaultPrompt);
                }}
              >
                Use the built-in prompt
              </button>
            ) : null}
            <span className="small muted">{prompt.length} / 6000</span>
          </div>
        </div>
      ) : null}

      {status ? (
        <div className="field" style={{ marginTop: 14 }}>
          <span className="field-label">HighLevel “Lead Rating” field</span>
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={settings.rating.pushToGhl}
              onChange={(event) =>
                save(
                  { pushToGhl: event.target.checked },
                  event.target.checked ? 'Ratings will be written to HighLevel.' : 'Ratings stay in the dashboard only.',
                )
              }
            />
            Write each rating to the contact’s Lead Rating field
          </label>
          <span className="small muted">
            Active-client sub-accounts with a contact field named “Lead Rating” get the score (e.g. “7.5”) as soon as a
            lead is rated, so HighLevel workflows and smart lists can use it. Needs the contacts.write permission.
          </span>
          {settings.rating.pushToGhl ? (
            <span className="small">
              {formatNumber(status.push.written)} in HighLevel · {formatNumber(status.push.waiting)} waiting
              {status.push.failing ? ` · ${formatNumber(status.push.failing)} failing` : ''}
              {status.push.lastRun ? ` · last written ${formatRelative(status.push.lastRun.at)}` : ''}
            </span>
          ) : null}
          {settings.rating.pushToGhl && status.push.lastError ? (
            <div className="inline-result bad">Last error: {status.push.lastError}</div>
          ) : null}
          {settings.rating.pushToGhl && status.push.missingField.length ? (
            <details className="small">
              <summary className="muted">
                {formatNumber(status.push.missingField.length)} active sub-account
                {status.push.missingField.length === 1 ? ' has' : 's have'} no Lead Rating field — add one in HighLevel to include
                {status.push.missingField.length === 1 ? ' it' : ' them'}
              </summary>
              <p className="muted" style={{ margin: '6px 0 0' }}>
                {status.push.missingField.map((location) => location.name).join(', ')}
              </p>
            </details>
          ) : null}
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 14 }}>
        <button type="button" className="btn btn-primary" disabled={busy || !settings.ready || status?.running} onClick={runNow}>
          {busy || status?.running ? 'Rating…' : 'Rate waiting leads now'}
        </button>
        <button
          type="button"
          className="btn"
          disabled={!settings.ready || status?.running}
          onClick={() => {
            if (window.confirm('Re-rate every lead with the current prompt? This uses AI requests, within the daily limit.')) void rerateAll();
          }}
        >
          Re-rate all leads
        </button>
        <span className="small muted">Up to 150 per run, within the daily limit.</span>
      </div>
      {note ? <div className="inline-result">{note}</div> : null}
    </div>
  );
}

/** Settings for the background call QA review, and where it has got to. */
function CallQaCard({
  settings,
  onSaved,
}: {
  settings: AiSettingsResponse;
  onSaved: (next: AiSettingsResponse, note: string) => void;
}) {
  const [status, setStatus] = useState<CallQaStatus | null>(null);
  const [limit, setLimit] = useState(String(settings.callQa.dailyLimit));
  const [minSeconds, setMinSeconds] = useState(String(settings.callQa.minSeconds));
  const [model, setModel] = useState(settings.callQa.model);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api
      .callQaStatus()
      .then((next) => {
        setStatus(next);
        setPrompt((current) => current ?? next.defaultPrompt);
      })
      .catch(() => undefined);
  }, []);
  useEffect(refresh, [refresh]);
  useEffect(() => {
    if (!status?.running) return;
    const timer = setInterval(refresh, 3000);
    return () => clearInterval(timer);
  }, [status?.running, refresh]);

  async function save(patch: Partial<AiSettingsResponse['callQa']>, message: string) {
    try {
      onSaved(await api.saveAiSettings({ callQa: patch }), message);
      refresh();
    } catch (cause) {
      setNote(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  const savedPrompt = status?.defaultPrompt ?? '';
  const promptChanged = prompt !== null && prompt.trim() !== savedPrompt.trim();

  async function savePrompt(value: string) {
    await save({ prompt: value.trim() }, 'Call QA scorecard saved.');
    setPrompt(null);
    if (
      status &&
      status.reviewed > 0 &&
      window.confirm('Review the calls of every sub-account that uses this scorecard again? It runs in the background, within the daily limit.')
    ) {
      try {
        const { cleared, status: next } = await api.resetCallQa('default');
        setStatus(next);
        setNote(`${formatNumber(cleared)} calls will be reviewed again.`);
      } catch (cause) {
        setNote(cause instanceof ApiError ? cause.message : String(cause));
      }
    }
  }

  async function runNow() {
    setBusy(true);
    setNote(null);
    try {
      const { result, status: next } = await api.runCallQa();
      setStatus(next);
      setNote(
        result.skipped
          ? `Not run: ${result.skipped}.`
          : result.error
            ? result.error
            : `Reviewed ${formatNumber(result.reviewed ?? 0)} calls${result.noTranscript ? `, ${result.noTranscript} have no transcript yet` : ''}${
                result.failed ? `, ${result.failed} could not be reviewed` : ''
              }.`,
      );
    } catch (cause) {
      setNote(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const active = settings.providers.find((item) => item.id === settings.activeProvider);
  const modelOptions = active ? [...new Set([...active.suggestedModels, active.model ?? active.defaultModel])] : [];

  return (
    <div className="card">
      <div className="card-head">
        <h2>Call QA</h2>
        <label className="switch">
          <input
            type="checkbox"
            role="switch"
            checked={settings.callQa.enabled}
            onChange={(event) => save({ enabled: event.target.checked }, `Call QA ${event.target.checked ? 'on' : 'off'}.`)}
          />
          {settings.callQa.enabled ? 'On' : 'Off'}
        </label>
      </div>
      <p className="card-sub">
        Reviews every answered call from its HighLevel transcript and scores how the team member handled it — opening,
        discovery, listening, pitch, objections, close and professionalism — with what the call achieved, strengths,
        misses and a coaching tip. Calls shorter than the minimum are not reviewed; they count as short calls on the
        Calls tab. Runs in the background every 10 minutes, newest calls first, one AI request per call.
      </p>

      {status ? (
        <div className="grid grid-kpi" style={{ marginBottom: 14 }}>
          <div className="stat">
            <span className="stat-label">Calls reviewed</span>
            <span className="stat-value">{formatNumber(status.reviewed)}</span>
            <span className="stat-foot">{status.averageScore !== null ? `average ${status.averageScore}/10` : 'none yet'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Waiting</span>
            <span className="stat-value">{formatNumber(status.waiting)}</span>
            <span className="stat-foot">{formatNumber(status.noTranscript)} without a transcript yet</span>
          </div>
          <div className="stat">
            <span className="stat-label">Reviewed in last 24h</span>
            <span className="stat-value">{formatNumber(status.reviewedLast24h)}</span>
            <span className="stat-foot">limit {formatNumber(status.dailyLimit)} a day</span>
          </div>
          <div className="stat">
            <span className="stat-label">Last run</span>
            <span className="stat-value" style={{ fontSize: 18 }}>
              {status.running ? 'Running…' : status.lastRun ? formatRelative(status.lastRun.at) : 'Not yet'}
            </span>
            <span className="stat-foot">
              {status.lastRun
                ? status.lastRun.error
                  ? `Stopped: ${status.lastRun.error}`
                  : `${formatNumber(status.lastRun.reviewed)} reviewed (${status.lastRun.trigger})`
                : settings.ready
                  ? 'First run within 10 minutes of start'
                  : 'Needs an AI key'}
            </span>
          </div>
        </div>
      ) : null}

      <div className="filters-body" style={{ padding: 0 }}>
        <div className="field">
          <label htmlFor="qa-min">Shortest call to review (seconds)</label>
          <div className="row nowrap-row">
            <input
              id="qa-min"
              type="text"
              inputMode="numeric"
              style={{ width: 90 }}
              value={minSeconds}
              onChange={(event) => setMinSeconds(event.target.value.replace(/\D/g, ''))}
            />
            {Number(minSeconds) !== settings.callQa.minSeconds && minSeconds !== '' ? (
              <button type="button" className="btn btn-sm" onClick={() => save({ minSeconds: Number(minSeconds) }, 'Minimum call length saved.')}>
                Save
              </button>
            ) : null}
          </div>
        </div>
        <div className="field">
          <label htmlFor="qa-window">Review calls from the last</label>
          <select
            id="qa-window"
            value={settings.callQa.windowDays}
            onChange={(event) => save({ windowDays: Number(event.target.value) }, 'Review window saved.')}
          >
            {[7, 14, 30, 60, 90].map((days) => (
              <option key={days} value={days}>
                {days} days
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="qa-limit">Most calls a day</label>
          <div className="row nowrap-row">
            <input
              id="qa-limit"
              type="text"
              inputMode="numeric"
              style={{ width: 110 }}
              value={limit}
              onChange={(event) => setLimit(event.target.value.replace(/\D/g, ''))}
            />
            {Number(limit) !== settings.callQa.dailyLimit && limit !== '' ? (
              <button type="button" className="btn btn-sm" onClick={() => save({ dailyLimit: Number(limit) }, 'Daily limit saved.')}>
                Save
              </button>
            ) : null}
          </div>
        </div>
        <div className="field field-grow">
          <label htmlFor="qa-model">Review model (optional)</label>
          <div className="row nowrap-row">
            <input
              id="qa-model"
              type="text"
              list="qa-models"
              placeholder={active ? `Same as lead rating: ${settings.rating.model || active.model || active.defaultModel}` : 'Set up a provider first'}
              value={model}
              onChange={(event) => setModel(event.target.value)}
              style={{ flex: '1 1 220px' }}
            />
            <datalist id="qa-models">
              {modelOptions.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
            {model.trim() !== settings.callQa.model ? (
              <button type="button" className="btn btn-sm" onClick={() => save({ model: model.trim() }, 'Review model saved.')}>
                Save
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {status && prompt !== null ? (
        <div className="field" style={{ marginTop: 14 }}>
          <label htmlFor="qa-prompt">Default scorecard</label>
          <textarea
            id="qa-prompt"
            rows={14}
            maxLength={6000}
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            style={{ fontFamily: 'inherit' }}
          />
          <span className="small muted">
            What a good call looks like for your setters and how to score each part. Keep the part names (opening,
            discovery, listening, pitch, objections, close, professionalism); the answer format is added automatically.
            Sub-accounts use this unless they have their own scorecard (Sub-accounts → AI setup).
          </span>
          <div className="row">
            <button type="button" className="btn btn-sm btn-primary" disabled={!promptChanged || !prompt.trim()} onClick={() => savePrompt(prompt)}>
              Save scorecard
            </button>
            {promptChanged ? (
              <button type="button" className="btn btn-sm" onClick={() => setPrompt(savedPrompt)}>
                Discard changes
              </button>
            ) : null}
            {status.prompt.trim() ? (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  if (window.confirm('Go back to the built-in scorecard?')) void savePrompt('');
                }}
              >
                Use the built-in scorecard
              </button>
            ) : null}
            <span className="small muted">{prompt.length} / 6000</span>
          </div>
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 14 }}>
        <button type="button" className="btn btn-sm" disabled={busy || !settings.ready || status?.running} onClick={runNow}>
          {busy ? 'Reviewing…' : 'Review waiting calls now'}
        </button>
      </div>
      {note ? <div className="inline-result">{note}</div> : null}
    </div>
  );
}
